import React, { useCallback, useEffect, useRef, useState } from 'react'
import Layout from '../../components/Layout/Layout'
import { callApi } from '../../api/callApi';
import { API_ENDPOINT, API_URL, API_URL_2 } from '../../api/constant';
import { IOrder } from '../../interfaces/order';
import Table from '../../components/UI/CommonTable/CommonTable';
import Pagination from '../../components/UI/List/Pagination';
import { useNavigate } from 'react-router-dom';
import moment from '../../utility/moment-timezone';
import { ISettings } from '../../interfaces/settings';
import StatusDropDown from '../../components/common/OrderStatusDropdown';
import { IAddress } from '../../interfaces/address';
import AssignDriverModal from './AssignDriverModal';
import { formatDateTime, showToastNotification } from '../../utility/helper';
import DateRangePickerComponent from '../../components/UI/Forms/DateRangePickerComponent';
import useConfirmationModal from '../../hooks/useConfirmationModal';
import ConfirmationModal from '../../components/UI/Modal/ConfirmationModal';
const POLL_INTERVAL_MS = 2 * 60 * 1000;
const Orders: React.FC = () => {
    const { showModal, modalProps, confirmAction } = useConfirmationModal();
    const [loading, setLoading] = useState<boolean>(false);
    const [orders, setOrders] = useState<IOrder[]>([]);
    const [currentPage, setCurrentPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);
    const [settings, setSettings] = useState<ISettings | null>(null);
    const [selectedOrders, setSelectedOrders] = useState<string[]>([]);
    const [driverModal, setDriverModal] = useState(false)
    const [activeTab, setActiveTab] = useState<'ongoing' | 'history' | 'refund' | 'unpaid'>('ongoing');
    const [search, setSearch] = useState('');
    const today = new Date()
    const [range, setRange] = useState<{
        startDate: Date;
        endDate: Date;
        key: string
    }[]>([
        {
            startDate: new Date(today.getFullYear(), today.getMonth(), 1),
            endDate: today,
            key: 'selection'
        }
    ]);

    const navigate = useNavigate()

    const intervalRef = useRef<number | null>(null);
    const reqIdRef = useRef<number>(0);

    useEffect(() => {
        if (!settings) {
            fetchSettings()
        }
    }, [settings]);

    const fetchSettings = async () => {
        try {
            const response = await callApi(API_URL + API_ENDPOINT.auth + 'settings', { method: 'GET' });
            if (response && response.length > 0) {
                let setting = response[0]
                setSettings(setting)
            }

        } catch (error: any) {
            console.error('Failed to fetch settings:', error.message);
        }
    };
    const getOrders = useCallback(async (page: number = 1) => {
        const currentReqId = ++reqIdRef.current;
        try {
            if (currentReqId === reqIdRef.current) setLoading(true);
            let params: string = '';
            if (activeTab === 'unpaid') {
                params = 'status=JUNK_INCOMPLETE'

            } else if (activeTab === 'ongoing') {
                params = 'isOnGoing=true'
            } else if (activeTab === 'refund') {
                params = 'isRefund=true'
            } else {
                params = 'isHistory=true'
            }
            if (search) {
                params += `&search=${encodeURIComponent(search)}`;
            }
            const selection = range[0];
            if (selection?.startDate && selection?.endDate) {
                const start = moment(selection.startDate).format("YYYY-MM-DD");
                const end = moment(selection.endDate).format("YYYY-MM-DD");
                params += `&startDate=${start}&endDate=${end}`;
            }
            const response = await callApi(
                API_URL_2 + API_ENDPOINT.order + `orders?page=${page}&sortType=DESC&${params}`,
                { method: 'GET' }
            );
            if (currentReqId === reqIdRef.current) {
                const { currentPage: respPage, data, totalPages: respTotal } = response;
                setOrders(data || []);
                setCurrentPage(respPage || page);
                setTotalPages(respTotal || 1);
            }
        } catch (error: any) {
            console.error('Failed to fetch orders:', error.message);
        } finally {
            if (currentReqId === reqIdRef.current) setLoading(false);
        }
    }, [activeTab, search, range]);

    const startPolling = useCallback(() => {
        if (intervalRef.current !== null) return;
        intervalRef.current = window.setInterval(() => {
            getOrders(1);
        }, POLL_INTERVAL_MS);
    }, [getOrders]);

    const stopPolling = useCallback(() => {
        if (intervalRef.current !== null) {
            clearInterval(intervalRef.current);
            intervalRef.current = null;
        }
    }, []);

    useEffect(() => {
        const handleVisibility = () => {
            if (document.visibilityState === 'visible') {
                getOrders(1);
                startPolling();
            } else {
                stopPolling();
            }
        };
        document.addEventListener('visibilitychange', handleVisibility);
        if (document.visibilityState === 'visible') {
            getOrders(1);
            startPolling();
        }
        return () => {
            document.removeEventListener('visibilitychange', handleVisibility);
            stopPolling();
        };
    }, [getOrders, startPolling, stopPolling]);

    useEffect(() => {
        getOrders(currentPage);
        stopPolling();
        if (document.visibilityState === 'visible') startPolling();
    }, [currentPage, activeTab]);



    const handleApplyFilters = () => {
        // Reset to first page when filters are applied
        setCurrentPage(1);
        getOrders(1);
        stopPolling();
        if (document.visibilityState === 'visible') startPolling();
    };



    const columns = [
        {
            header: "Select",
            accessor: "id",
            render: (id: string, order: IOrder) => (
                <input
                    type="checkbox"
                    checked={selectedOrders.includes(id)}
                    className='form-check-input'
                    onChange={(e) => {
                        console.log("checked", selectedOrders)
                        if (e.target.checked) {
                            console.log("checked", selectedOrders)
                            setSelectedOrders([...selectedOrders, id]);
                        } else {
                            console.log("un checked", selectedOrders)
                            setSelectedOrders(selectedOrders.filter(orderId => orderId !== id));
                        }
                    }}
                    disabled={order.status !== "ACCEPTED"}
                />
            )
        },
        { header: "Order ID", accessor: "orderId" },
        {
            header: "Status", accessor: "status", render: (status: string, order: IOrder) =>
                <StatusDropDown
                    CurrentStatus={status}
                    orderId={order.id}
                    orderStatus={settings?.orderStatus || []}
                    previousStatus={order.previousStatus}
                    // driverDetail={order?.driverId ?? null}
                    updateStatus={() => getOrders(currentPage)} />
        },
        { header: 'Delivery Time', accessor: 'deliveryTime', render: (deliveryTime: string) => formatDateTime(deliveryTime) },
        // {
        //     header: 'Driver Performance Status', accessor: 'deliveryStatus', render: (deliveryStatus: string) =>
        //         <>
        //             {
        //                 deliveryStatus === 'ON TIME' ?
        //                     <span className="btn btn-success px-2 py-1 text-white">{deliveryStatus}</span> :
        //                     deliveryStatus === 'DELAYED' ?
        //                         <span className="btn btn-danger px-2 py-1 text-white">{deliveryStatus}</span> :
        //                         ' -'
        //             }
        //         </>
        // },
        { header: "Total", accessor: "total" },
        { header: "Discount", accessor: "discountedValue" },
        { header: "Grand Total", accessor: "grandTotal" },
        // { header: "Driver Assigned", accessor: "driverName" },
        {
            header: "Delivery Address", accessor: "address", render: (address: IAddress) => {
                const addressLine = [
                    address.buildingName,
                    address.aptNumber,
                    address.floor,
                    address.street,
                    address.block,
                    address?.companyName,
                    address.additionalDirection,
                    address.phone,
                ]
                    .filter((item) => item && item.trim() !== "") // Exclude empty or null fields
                    .join(", ");
                return addressLine
            }
        },
        { header: "Created At", accessor: "createdAt", render: (createdAt: string) => moment(createdAt).format("DD MMM, YYYY hh:mm a") },
    ];

    const PaymentColumn = [{
        header: "Payment Status", accessor: "paymentStatus"
    }]

    const actions = [
        {
            icon: "bi bi-eye",
            color: "success",
            onClick: (order: IOrder) => navigate(`/order-details/${order.id}`),
            tooltip: 'View Order'
        },
        //Approve Payment action 
        {
            icon: "bi bi-check-circle",
            color: "warning",
            onClick: (order: IOrder) => confirmAction({
                title: 'Confirm Payment Approval',
                message: 'Are you sure you want to approve this payment? Approve only if verified — this action cannot be undone.',
                onConfirm: () => handleApprovePayment(order),
            }),
            show: (order: IOrder) => order.paymentStatus != "PAID",
            tooltip: 'Approve Payment'
        },
        // {
        //     icon: "bi bi-truck",
        //     color: "warning",
        //     onClick: (order: IOrder) => order.status === "ACCEPTED" && handleSingleAssign(order.id),
        //     show: (order: IOrder) => order.status === "ACCEPTED",
        //     tooltip: 'Assign Driver'
        // }
    ];



    const handlePageChange = (page: number) => {
        setCurrentPage(page);
    };
    const handleApprovePayment = async (order: IOrder) => {
        try {
            setLoading(true);
            const response = await callApi(API_URL_2 + API_ENDPOINT.order + `orders/update-status`, {
                method: 'PUT',
                data: {
                    id: order.id,
                    paymentStatus: 'PAID'
                }
            });
            if (response) {
                // Refresh orders after approving payment
                getOrders(currentPage);
            }
        } catch (error: any) {
            console.error('Failed to approve payment:', error.message);
        } finally {
            setLoading(false);
        }
    }
    // const handleSingleAssign = (orderId: string) => {
    //     setSelectedOrders([orderId]);
    //     setDriverModal(true);
    // }



    const handleDownloadReport = async () => {
        let params = '';
        const selection = range[0];

        if (selection?.startDate && selection?.endDate) {
            const start = moment(selection.startDate).format("YYYY-MM-DD");
            const end = moment(selection.endDate).format("YYYY-MM-DD");
            params += `startDate=${start}&endDate=${end}`;
        }

        if (activeTab === 'history') {
            params += '&isDelivered=true';
        } else if (activeTab === 'refund') {
            params += '&status=REFUNDED';
        } else {
            params += ''
        }

        try {
            const response = await callApi(API_URL_2 + API_ENDPOINT.order + 'orders/download-report?' + params, {
                method: 'POST',
                responseType: 'blob'
            });

            if (!response) {
                throw new Error('Failed to generate report');
            }

            const blob = new Blob([response], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
            const url = window.URL.createObjectURL(blob);

            const a = document.createElement('a');
            a.href = url;
            a.download = `Order_Report_${Date.now()}.xlsx`;
            document.body.appendChild(a);
            a.click();
            document.body.removeChild(a);
            window.URL.revokeObjectURL(url);
        } catch (error) {
            console.error('Error downloading the report:', error);
        }
    };

    const handleDownloadProductReport = async () => {
        try {
            const response = await callApi(API_URL_2 + API_ENDPOINT.order + 'orders/download-product-sales-report', {
                method: 'POST',
                responseType: 'blob'
            });

            if (!response) {
                throw new Error('Failed to generate product sales report');
            }

            const blob = new Blob([response], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
            const url = window.URL.createObjectURL(blob);

            const a = document.createElement('a');
            a.href = url;
            a.download = `Product_Sales_Report_${Date.now()}.xlsx`;
            document.body.appendChild(a);
            a.click();
            document.body.removeChild(a);
            window.URL.revokeObjectURL(url);
        } catch (error) {
            showToastNotification('danger', 'Error downloading the product sales report.');
            console.error('Error downloading the product sales report:', error);
        }
    }

    const filteredColumns = columns.filter(column => {
        if (column.header === "Select") {
            return activeTab === 'ongoing';
        } else if (column.header === "Driver Performance Status") {
            return activeTab !== 'ongoing'
        }
        return true; // Show all other columns
    });

    return (
        <Layout>
            <div className='d-flex justify-content-between align-items-start'>
                <h1 className="app-page-title">Orders</h1>
                <div className='d-flex gap-2'>
                    {/* {selectedOrders.length > 0 && <button onClick={() => setDriverModal(true)} className='btn app-btn-primary d-flex align-items-center gap-1'><svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 12 12" fill="none">
                        <path fillRule="evenodd" clipRule="evenodd" d="M6 0C6.13261 0 6.25979 0.0526785 6.35355 0.146447C6.44732 0.240215 6.5 0.367392 6.5 0.5V5.5H11.5C11.6326 5.5 11.7598 5.55268 11.8536 5.64645C11.9473 5.74021 12 5.86739 12 6C12 6.13261 11.9473 6.25979 11.8536 6.35355C11.7598 6.44732 11.6326 6.5 11.5 6.5H6.5V11.5C6.5 11.6326 6.44732 11.7598 6.35355 11.8536C6.25979 11.9473 6.13261 12 6 12C5.86739 12 5.74021 11.9473 5.64645 11.8536C5.55268 11.7598 5.5 11.6326 5.5 11.5V6.5H0.5C0.367392 6.5 0.240215 6.44732 0.146447 6.35355C0.0526785 6.25979 0 6.13261 0 6C0 5.86739 0.0526785 5.74021 0.146447 5.64645C0.240215 5.55268 0.367392 5.5 0.5 5.5H5.5V0.5C5.5 0.367392 5.55268 0.240215 5.64645 0.146447C5.74021 0.0526785 5.86739 0 6 0Z" fill="white" />
                    </svg> Assign Driver</button>} */}
                    <button className='btn app-btn-primary d-flex align-items-center gap-1' onClick={() => handleDownloadReport()} >
                        <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="currentColor" className="bi bi-download" viewBox="0 0 16 16">
                            <path d="M.5 9.9a.5.5 0 0 1 .5.5v2.5a1 1 0 0 0 1 1h12a1 1 0 0 0 1-1v-2.5a.5.5 0 0 1 1 0v2.5a2 2 0 0 1-2 2H2a2 2 0 0 1-2-2v-2.5a.5.5 0 0 1 .5-.5" />
                            <path d="M7.646 11.854a.5.5 0 0 0 .708 0l3-3a.5.5 0 0 0-.708-.708L8.5 10.293V1.5a.5.5 0 0 0-1 0v8.793L5.354 8.146a.5.5 0 1 0-.708.708z" />
                        </svg>
                        Download Report
                    </button>
                    <button className='btn app-btn-primary d-flex align-items-center gap-1' onClick={() => handleDownloadProductReport()}>
                        <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="currentColor" className="bi bi-download" viewBox="0 0 16 16">
                            <path d="M.5 9.9a.5.5 0 0 1 .5.5v2.5a1 1 0 0 0 1 1h12a1 1 0 0 0 1-1v-2.5a.5.5 0 0 1 1 0v2.5a2 2 0 0 1-2 2H2a2 2 0 0 1-2-2v-2.5a.5.5 0 0 1 .5-.5" />
                            <path d="M7.646 11.854a.5.5 0 0 0 .708 0l3-3a.5.5 0 0 0-.708-.708L8.5 10.293V1.5a.5.5 0 0 0-1 0v8.793L5.354 8.146a.5.5 0 1 0-.708.708z" />
                        </svg>
                        Product Order Report
                    </button>
                </div>
            </div>
            <div className="tabs-container mb-3 gap-2 d-flex">
                <button
                    className={`tab-button btn app-btn-primary-outline  ${activeTab === 'ongoing' ? 'app-btn-primary' : ''}`}
                    onClick={() => {
                        setActiveTab('ongoing');
                        setCurrentPage(1);
                    }}>Ongoing Orders</button>
                <button
                    className={`tab-button btn app-btn-primary-outline  ${activeTab === 'history' ? 'app-btn-primary' : ''}`}
                    onClick={() => {
                        setActiveTab('history')
                        setCurrentPage(1)
                    }}>History</button>
                <button
                    className={`tab-button btn app-btn-primary-outline  ${activeTab === 'refund' ? 'app-btn-primary' : ''}`}
                    onClick={() => {
                        setActiveTab('refund')
                        setCurrentPage(1)
                    }}>Refund Orders</button>
                <button
                    className={`tab-button btn app-btn-primary-outline  ${activeTab === 'unpaid' ? 'app-btn-primary' : ''}`}
                    onClick={() => {
                        setActiveTab('unpaid')
                        setCurrentPage(1)
                    }}>Unpaid/Failed Orders</button>
            </div>
            {/* Filters Section */}
            <div className="row mb-3">
                <div className='col-5'>
                    <input
                        type="text"
                        placeholder="Search Orders"
                        className="form-control"
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                    />
                </div>
                <div className='col-5' style={{ position: 'relative' }}>
                    {/* This input shows the selected date range and toggles the popover */}

                    <DateRangePickerComponent
                        initialStartDate={range[0].startDate}
                        initialEndDate={today}
                        onChange={(start, end) => {
                            console.log('Selected Range: data', start, end);
                            setRange([
                                {
                                    startDate: start,
                                    endDate: end,
                                    key: 'selection'
                                }
                            ]);

                        }}
                    />
                </div>
                <div className='col-auto'>
                    <button className="btn btn-primary" onClick={handleApplyFilters}>
                        Apply Filters
                    </button>
                </div>
            </div>
            <div className="app-card app-card-orders-table shadow-sm mb-4 p-4">
                <div className="app-card-body">
                    <Table
                        columns={activeTab === 'unpaid' ? [...columns, ...PaymentColumn] : filteredColumns}
                        data={orders}
                        loading={loading}
                        actions={actions}
                        emptyMessage="No Orders available."
                    />
                    <Pagination
                        currentPage={currentPage}
                        totalPages={totalPages}
                        onPageChange={handlePageChange}
                    />
                </div>
            </div>
            <AssignDriverModal modal={driverModal} close={() => {
                setDriverModal(false);
                setSelectedOrders([]);
                getOrders(currentPage)
            }} selectedOrders={selectedOrders} />
            <ConfirmationModal {...modalProps} show={showModal} />
        </Layout >
    )
}

export default Orders